import { useEffect, useState } from "react";

import { useAssets } from "@/features/matches";

import { loadImage } from "../../map/loadImage";
import type { MapColors } from "../../map/MapColors";
import type { MapScene, SceneCamp } from "../../map/MapScene";
import { recolorMinimap } from "../../map/recolorMinimap";
import { tintIcon } from "../../map/tintIcon";

type LoadedImages = {
  minimap: CanvasImageSource | null;
  camps: ReadonlyMap<string, CanvasImageSource>;
  heroes: ReadonlyMap<number, CanvasImageSource>;
};

const EMPTY: LoadedImages = { minimap: null, camps: new Map(), heroes: new Map() };
// У иконок лагерей поля: рисунок увеличиваем внутри кадра, как в макете.
const CAMP_ICON_ZOOM = 1.7;

// Картинки карты: миникарта в игровых цветах, иконки лагерей в цветах «кто фармил»,
// лица героев. Грузятся в фоне; не загрузилась картинка — карта рисуется без неё.
export const useMapImages = (scene: MapScene, colors: MapColors): LoadedImages => {
  const [images, setImages] = useState<LoadedImages>(EMPTY);
  const { data: assets } = useAssets();

  useEffect(() => {
    let cancelled = false;
    const statusColor: Record<SceneCamp["status"], string> = {
      me: colors.me,
      team: colors.mate,
      missed: colors.missed,
      enemy: colors.enemy,
    };
    const load = async () => {
      const minimap = await loadImage(scene.minimapUrl)
        .then((image) => recolorMinimap(image, colors))
        .catch(() => null);
      const camps = new Map<string, CanvasImageSource>();
      const icons = new Map(
        scene.camps.flatMap((camp) => (camp.iconUrl === null ? [] : [[camp.kind, camp.iconUrl]])),
      );
      await Promise.all(
        [...icons].map(async ([kind, url]) => {
          const image = await loadImage(url).catch(() => null);
          if (image === null) {
            return;
          }
          for (const [status, color] of Object.entries(statusColor)) {
            camps.set(
              `${kind}:${status}`,
              tintIcon(image, color, colors.ink, { zoom: CAMP_ICON_ZOOM }),
            );
          }
        }),
      );
      const heroIds = new Set([scene.me.heroId, ...scene.others.map((player) => player.heroId)]);
      const heroes = new Map<number, CanvasImageSource>();
      await Promise.all(
        [...heroIds].map(async (heroId) => {
          const url = assets?.heroes.find((hero) => hero.id === heroId)?.icon_url;
          const image =
            url === null || url === undefined ? null : await loadImage(url).catch(() => null);
          if (image !== null) {
            heroes.set(heroId, image);
          }
        }),
      );
      if (!cancelled) {
        setImages({ minimap, camps, heroes });
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [scene, colors, assets]);

  return images;
};
